// ─── Google Maps Platform helpers ─────────────────────────────────────────
// Thin wrappers around the Google Maps APIs Proxy uses beyond Geocoding:
//   - Static Maps (staticMapUrl)         → thumbnail images in LOI emails
//   - Distance Matrix (drivingTimes)     → batch drive time / distance
//   - Places nearby (nearestByType)      → find nearest airport/port/etc
//   - Directions (drivingRoute)          → drive-time + route to one target
//
// All calls use REACT_APP_GOOGLE_GEOCODE_KEY (single key with Geocoding,
// Maps JS, Places, Directions, Distance Matrix, Static Maps enabled). The
// key restriction is API-list only (no HTTP-referer) because the REST
// endpoints don't accept referer-restricted keys.

const KEY = () => process.env.REACT_APP_GOOGLE_GEOCODE_KEY || process.env.REACT_APP_GOOGLE_MAPS_KEY;

// ── Static Maps ──────────────────────────────────────────────────────────
// Returns a URL that renders a 400x250 static satellite map with a red pin
// on each of the provided coordinates. Center + zoom are auto-fit when
// multiple points are provided.
export function staticMapUrl(points, { width = 480, height = 260, mapType = "satellite", zoom = null } = {}) {
  const key = KEY();
  if (!key || !points || points.length === 0) return null;
  const clean = points.filter(p => p && Number.isFinite(p.lat) && Number.isFinite(p.lng));
  if (clean.length === 0) return null;
  const markers = clean
    .map((p, i) => `markers=color:red%7Clabel:${i < 26 ? String.fromCharCode(65 + i) : ""}%7C${p.lat},${p.lng}`)
    .join("&");
  const params = [
    `size=${width}x${height}`,
    `maptype=${mapType}`,
    `scale=2`,   // retina — sharper on high-DPI displays
    markers,
    `key=${key}`,
  ];
  // Auto-fit if not zoomed; single-point mode: center + zoom 15
  if (clean.length === 1 && zoom == null) {
    params.unshift(`center=${clean[0].lat},${clean[0].lng}`, `zoom=15`);
  } else if (zoom != null && clean.length >= 1) {
    params.unshift(`center=${clean[0].lat},${clean[0].lng}`, `zoom=${zoom}`);
  }
  return `https://maps.googleapis.com/maps/api/staticmap?${params.join("&")}`;
}

// ── Distance Matrix ──────────────────────────────────────────────────────
// Fetches driving distance + duration from one origin to many destinations,
// batched in groups of 10 (Distance Matrix's per-request destination cap).
// Returns { [destKey]: { distanceText, distanceMeters, durationText, durationSec } | null }
export async function drivingTimes(origin, destinations, { keyFn = (d, i) => String(i), keyName = "REACT_APP_GOOGLE_GEOCODE_KEY" } = {}) {
  const apiKey = KEY();
  if (!apiKey) throw new Error(`Missing ${keyName} env var`);
  if (!origin?.lat || !origin?.lng) throw new Error("Origin lat/lng required");
  const out = {};
  const batches = [];
  for (let i = 0; i < destinations.length; i += 10) batches.push(destinations.slice(i, i + 10));
  for (let bi = 0; bi < batches.length; bi++) {
    const batch = batches[bi];
    const dst = batch.map(d => `${d.lat},${d.lng}`).join("|");
    const url = `https://maps.googleapis.com/maps/api/distancematrix/json?origins=${origin.lat},${origin.lng}&destinations=${encodeURIComponent(dst)}&units=imperial&key=${apiKey}`;
    const r = await fetch(url);
    const j = await r.json();
    if (j.status !== "OK") {
      console.warn("Distance Matrix batch failed:", j.status, j.error_message);
      // still emit nulls for these keys so callers see "—" rather than missing entries
      batch.forEach((d, i) => { out[keyFn(d, bi * 10 + i)] = null; });
      continue;
    }
    const row = j.rows?.[0]?.elements || [];
    batch.forEach((d, i) => {
      const el = row[i];
      if (!el || el.status !== "OK") { out[keyFn(d, bi * 10 + i)] = null; return; }
      out[keyFn(d, bi * 10 + i)] = {
        distanceText: el.distance?.text || null,
        distanceMeters: el.distance?.value || null,
        durationText: el.duration?.text || null,
        durationSec: el.duration?.value || null,
      };
    });
  }
  return out;
}

// ── Places: nearest of a type ────────────────────────────────────────────
// Uses the Places Nearby Search REST endpoint to find the closest instance
// of a Google place type (e.g. "airport", "port", "train_station") within
// a max radius. Returns { name, address, lat, lng, place_id } or null.
export async function nearestByType(origin, type, { radius = 50000 } = {}) {
  const apiKey = KEY();
  if (!apiKey || !origin?.lat || !origin?.lng) return null;
  const url = `https://maps.googleapis.com/maps/api/place/nearbysearch/json?location=${origin.lat},${origin.lng}&rankby=distance&type=${encodeURIComponent(type)}&key=${apiKey}`;
  // Note: rankby=distance ignores radius but returns closest 20 results.
  const r = await fetch(url);
  const j = await r.json();
  if (j.status !== "OK") { console.warn("Places nearby failed:", j.status, j.error_message); return null; }
  const p = j.results?.[0];
  if (!p) return null;
  return {
    name: p.name,
    address: p.vicinity || p.formatted_address || "",
    lat: p.geometry?.location?.lat,
    lng: p.geometry?.location?.lng,
    place_id: p.place_id,
  };
}

// ── Directions: driving route to one destination ─────────────────────────
// Returns { distanceText, distanceMeters, durationText, durationSec } or null
export async function drivingRoute(origin, destination) {
  const apiKey = KEY();
  if (!apiKey || !origin?.lat || !origin?.lng || !destination?.lat || !destination?.lng) return null;
  const url = `https://maps.googleapis.com/maps/api/directions/json?origin=${origin.lat},${origin.lng}&destination=${destination.lat},${destination.lng}&units=imperial&key=${apiKey}`;
  const r = await fetch(url);
  const j = await r.json();
  if (j.status !== "OK") { console.warn("Directions failed:", j.status, j.error_message); return null; }
  const leg = j.routes?.[0]?.legs?.[0];
  if (!leg) return null;
  return {
    distanceText: leg.distance?.text || null,
    distanceMeters: leg.distance?.value || null,
    durationText: leg.duration?.text || null,
    durationSec: leg.duration?.value || null,
  };
}
